import type { FC } from 'react';
import {
  Controller,
  DefaultValues,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import { Avatar, chakra, VStack } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { BICtoBankInfo } from '@shinetools/iban-bban-bic';
import { printFormat } from 'iban';

import Button from 'components/_core/Button';
import * as Form from 'components/_core/form';
import Heading from 'components/_core/Heading';
import SunshineCard from 'components/_core/SunshineCard';
import Text from 'components/_core/Text';
import ReceiptUpload from 'components/ReceiptUpload';
import StepperProgressBar from 'components/Stepper/StepperProgressBar';
import * as Layout from 'layouts/StickyStepperLayout';

import { State } from '../../../../../BankTransferRequestsNewProvider/reducer';
import locales from './locales';
import {
  TransferInformationFormSchema,
  transferInformationFormSchema,
} from './schema';

interface TransferInformationFormProps {
  onRecipientCardClick?: () => void;
  onSubmit: SubmitHandler<TransferInformationFormSchema>;
  recipientInformation: State['recipientInformation'];
  defaultValues?: DefaultValues<TransferInformationFormSchema>;
}

const TransferInformationForm: FC<TransferInformationFormProps> = ({
  defaultValues,
  onRecipientCardClick,
  onSubmit,
  recipientInformation,
}) => {
  const form = useForm<TransferInformationFormSchema>({
    defaultValues,
    mode: 'onChange',
    resolver: zodResolver(transferInformationFormSchema),
  });

  const { isSubmitting, isValid } = form.formState;

  const file = form.watch('supportingDocument.fileRef');
  const hasUploadedFile = file !== undefined && file !== null;

  return (
    <chakra.form display="contents" onSubmit={form.handleSubmit(onSubmit)}>
      <Layout.ContentsContainer gap="space-40">
        <VStack align="flex-start" spacing="space-16">
          <Heading size="lg">{locales.title}</Heading>
          <Text size="lg">{locales.description}</Text>
        </VStack>
        <VStack alignItems="stretch" spacing="space-24">
          <Form.Field
            error={form.formState.errors.amount?.message}
            label={locales.labels.amount}
          >
            <Form.Input
              inputMode="numeric"
              placeholder={locales.placeholders.amount}
              step={0.01}
              type="number"
              {...form.register('amount', {
                valueAsNumber: true,
              })}
            />
          </Form.Field>
          {recipientInformation.name &&
          recipientInformation.iban &&
          recipientInformation.swiftBic ? (
            <Form.Field label={locales.labels.recipient}>
              <SunshineCard
                alignItems="center"
                onClick={onRecipientCardClick}
                title={recipientInformation.name}
              >
                <SunshineCard.Slot name="aside">
                  <Avatar
                    background="grey.100"
                    borderColor="border"
                    borderRadius="radius-10"
                    borderWidth="thin"
                    boxSize="space-32"
                    color="text-primary"
                    fontWeight="weight-500"
                    name={recipientInformation.name}
                  />
                </SunshineCard.Slot>
                <SunshineCard.Content>
                  <SunshineCard.Text>
                    {BICtoBankInfo(recipientInformation.swiftBic).name}{' '}
                    {printFormat(recipientInformation.iban)}
                  </SunshineCard.Text>
                </SunshineCard.Content>
                <SunshineCard.Slot name="action">
                  <SunshineCard.SunshineIcon name="chevron-right" />
                </SunshineCard.Slot>
              </SunshineCard>
            </Form.Field>
          ) : null}
          <Form.Field
            error={form.formState.errors.label?.message}
            hint={locales.hints.label}
            isRequired={false}
            label={locales.labels.label}
          >
            <Form.Input {...form.register('label')} />
          </Form.Field>
          <Form.Field label={locales.labels.supportingDocument}>
            <Controller
              control={form.control}
              name="supportingDocument"
              render={({ field }) => (
                <ReceiptUpload
                  onReceiptRemoved={() => {
                    field.onChange(null);
                  }}
                  onReceiptUploaded={field.onChange}
                  receipt={field.value}
                />
              )}
            />
          </Form.Field>
        </VStack>
        {!hasUploadedFile ? (
          <VStack alignItems="flex-start" spacing="space-24">
            <Text variant="secondary">
              {locales.supportingDocumentTypes.label}
            </Text>
            <VStack
              alignItems="flex-start"
              as="ol"
              color="text-primary"
              spacing="space-16"
              sx={{
                '& li': {
                  '&:before': {
                    alignItems: 'center',
                    background: 'grey.200',
                    borderRadius: 'full',
                    content: 'counter(types-counter)',
                    display: 'inline-flex',
                    fontSize: 'font-12',
                    fontWeight: 500,
                    height: 'space-20',
                    justifyContent: 'center',
                    lineHeight: '20',
                    marginRight: 'space-16',
                    width: 'space-20',
                  },
                  counterIncrement: 'types-counter',
                },
                counterReset: 'types-counter',
                listStyle: 'none',
              }}
            >
              <li>{locales.supportingDocumentTypes.items['0']}</li>
              <li>{locales.supportingDocumentTypes.items['1']}</li>
            </VStack>
          </VStack>
        ) : null}
      </Layout.ContentsContainer>
      <Layout.FooterContainer>
        <StepperProgressBar />

        <Layout.FooterButtonsContainer>
          <Button
            isDisabled={!isValid || isSubmitting}
            isLoading={isSubmitting}
            type="submit"
          >
            {locales.submit}
          </Button>
        </Layout.FooterButtonsContainer>
      </Layout.FooterContainer>
    </chakra.form>
  );
};

export default TransferInformationForm;
